import React from "react";
import { Box, TextField, MenuItem } from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import VideocamIcon from "@material-ui/icons/Videocam";
import {
  G, G_SOFT, INK, BODY, MUTED, ACCOUNT, ACCOUNT_SOFT, CONTACT, CONTACT_SOFT,
  SELECTABLE_CURRENCIES,
} from "./tokens";
import { familyLabel, findByKey } from "./checkoutCatalog";
import { pvQty, pvUnit, pvTitle } from "./pricingLines";

// The three things a buyer ends up with: a card, a delivery list, and a bill.
const buildReceives = ({ sub, credits, webinar, creditFields, catalog }) => {
  const out = [];
  if (sub && (sub.planKey || (sub.addOns || []).length)) {
    const p = sub.planKey ? findByKey(catalog, sub.planKey) : null;
    // One name per FAMILY, not per entry: flows carry two entries, which would
    // otherwise read "Flow Builder + Flow Builder".
    const add = [...new Set((sub.addOns || []).map((a) => familyLabel(catalog, a.family)))];
    const head = p ? `${p.label} subscription` : "Add-on subscription";
    out.push({
      Ico: AutorenewIcon, bg: ACCOUNT_SOFT, fg: ACCOUNT,
      t: `${head}${add.length ? " + " + add.join(" + ") : ""}`,
      d: "Renews each term · activated after they redeem in their account",
    });
  }
  if (credits) {
    creditFields.forEach((c) => {
      const v = Number(credits[c.k]) || 0;
      if (v > 0) out.push({ Ico: AccountBalanceWalletIcon, bg: G_SOFT, fg: "#0f7a3d", t: `${v.toLocaleString("en-IN")} ${c.name}`, d: "Added to their balance after they redeem" });
    });
  }
  if (webinar) out.push({ Ico: VideocamIcon, bg: CONTACT_SOFT, fg: CONTACT, t: "Webinar access link", d: "Emailed instantly after payment" });
  return out;
};

const BuyerPreview = ({
  classes, form, set, sub, credits, webinar, creditFields, catalog,
  lines, base, pricingReached, money, barWidth,
}) => {
  const receives = buildReceives({ sub, credits, webinar, creditFields, catalog });

  return (
    <Box className={classes.preview}>
      <div>
        <div className={classes.pvLabel}>What the buyer sees</div>
        <Box className={classes.card} p={"18px"}>
          <div className={classes.ccTitle}>{form.planTitle || "Untitled product"}</div>
          <div className={classes.ccSub}>{form.planSubtitle}</div>
          <ul className={classes.ccFeats}>
            {form.features.map((f, i) => (
              <li key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: BODY }}><span style={{ color: G, fontWeight: 800 }}>✓</span>{f}</li>
            ))}
          </ul>
          <div className={classes.ccPrice}>
            {pricingReached ? (
              <span className={classes.ccBig}>{money(base)}</span>
            ) : (
              <span className={classes.priceSlot}>
                <LockOutlinedIcon style={{ fontSize: 13 }} /> Price set in step 3
              </span>
            )}
            <span className={classes.ccPer}>{form.billing.label}</span>
          </div>
        </Box>
      </div>

      <div>
        <div className={classes.pvLabel}>On purchase, customer receives</div>
        <Box className={classes.card} p={"16px 18px"} display="flex" flexDirection="column" style={{ gap: 12 }}>
          {receives.length === 0 && <div className={classes.hint}>Nothing selected yet — turn on an entitlement in step 2.</div>}
          {receives.map((r, i) => {
            const Ico = r.Ico;
            return (
              <Box key={i} className={classes.recvItem}>
                <Box className={classes.recvIco} style={{ background: r.bg, color: r.fg }}><Ico style={{ fontSize: 16 }} /></Box>
                <Box><div style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{r.t}</div><div style={{ fontSize: 11.5, color: MUTED, marginTop: 3 }}>{r.d}</div></Box>
              </Box>
            );
          })}
        </Box>
      </div>

      <div>
        {/* The stored line items, as the invoice renders them — not a buyer
            view (that's the single-price card above). Labelled so a
            subscription billing as several items doesn't look like a mistake. */}
        <div className={classes.pvLabel}>
          {pricingReached ? "How this bills" : "How this will bill"}
          {!pricingReached && lines.length > 0 && (
            <span className={classes.pvCount}>{lines.length} line{lines.length === 1 ? "" : "s"}</span>
          )}
        </div>
        <Box className={classes.card} p={"16px 18px"}>
          <div className={classes.hint} style={{ marginBottom: 10 }}>
            {pricingReached
              ? "What the customer's invoice will show."
              : "Amounts are set in step 3."}
          </div>
          {lines.length === 0 && <Box className={classes.bline}><span style={{ color: MUTED }}>No priced lines yet</span><span /></Box>}

          {pricingReached ? (
            <>
              {lines.length > 0 && (
                <Box className={classes.blineHead}>
                  <span />
                  <span className={classes.pvNum}>Qty</span>
                  <span className={classes.pvNum}>Unit</span>
                  <span className={classes.pvNum}>Amount</span>
                </Box>
              )}
              {lines.map((l, i) => (
                <Box key={i} className={classes.blineGrid}>
                  <span>{pvTitle(l)}</span>
                  <span className={classes.pvNum}>{pvQty(l).toLocaleString("en-IN")}</span>
                  <span className={classes.pvNum}>{money(pvUnit(l))}</span>
                  <span className={classes.pvNum}>{money(l.amount)}</span>
                </Box>
              ))}
              <Box className={`${classes.blineGrid} ${classes.blineGridTot}`}>
                <span>Total</span>
                <span />
                <span />
                <span className={classes.pvNum}>{money(base)}</span>
              </Box>
            </>
          ) : (
            <>
              {lines.map((l, i) => (
                <Box key={i} className={classes.bline}>
                  <span>{l.title || "Untitled line"}</span>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    <span className={classes.amtBar} style={{ width: barWidth(l.amount) }} />
                  </span>
                </Box>
              ))}
              <Box className={`${classes.bline} ${classes.blineTot}`}>
                <span>Total</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  <span className={`${classes.amtBar} ${classes.amtBarTot}`} style={{ width: barWidth(base) }} />
                </span>
              </Box>
            </>
          )}
        </Box>
      </div>

      <div>
        <div className={classes.pvLabel}>Currency</div>
        <TextField className={classes.input} select variant="outlined" size="small"
          value={form.currency} onChange={(e) => set({ currency: e.target.value })} style={{ minWidth: 120 }}>
          {SELECTABLE_CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
      </div>
    </Box>
  );
};

export default BuyerPreview;
